import React, { useEffect, useState } from 'react';
import { ChakraProvider, Box, VStack, Flex, Spinner, Text } from '@chakra-ui/react';
import { extendTheme } from '@chakra-ui/react';

// Import components
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import MusicSection from './components/MusicSection';
import VideoSection from './components/VideoSection';
import Footer from './components/Footer';

// Import the local data file
import appData from './data.json';

// Custom Chakra UI Theme for "Space Retro" feel
const customTheme = extendTheme({
  colors: {
    // Defining a specific "space" palette
    space: {
      deepBlue: '#6c0000',      // Darkest background, like deep space
      midnight: '#910404',      // Slightly lighter, for card backgrounds/elements
      slate: '#903c3c',         // A mid-tone gray-blue
      moonRock: '#A9B4C2',      // Muted light gray, like lunar surface
      starlight: '#E0E0E0',     // Off-white for primary text, subtle glow
      rocketRed: '#E4572E',     // A vibrant, but slightly desaturated red for accents
      cosmicOrange: '#FFC914',  // A warm, vintage orange
      warningYellow: '#F2D73A', // For subtle highlights or warnings
    },
    // Mapping generic brand colors to our space palette for easier use in components
    brand: {
      900: 'space.deepBlue',      // Main background
      800: 'space.midnight',      // Component background
      700: 'space.slate',         // Borders, dividers
      600: 'space.moonRock',      // Secondary text
      500: 'space.starlight',     // Primary text
      400: 'space.warningYellow', // Minor accents
      300: 'space.starlight',     // Lighter text
      accent: 'space.rocketRed',  // Primary action/highlight color
      secondaryAccent: 'space.cosmicOrange', // Secondary action/highlight color
    },
  },
  fonts: {
    heading: `'Space Mono', monospace`, // Retro-futuristic, monospaced font for headings
    body: `'Outfit', sans-serif`,       // Clean, modern sans-serif for body text
  },
  styles: {
    global: (props) => ({
      body: {
        // Subtle radial gradient to mimic deep space/nebula
        bg: `radial-gradient(circle at top left, ${props.theme.colors.space.slate} 0%, ${props.theme.colors.space.deepBlue} 50%, ${props.theme.colors.space.midnight} 100%)`,
        color: props.colorMode === 'dark' ? 'space.starlight' : 'space.deepBlue', // Dynamic text color
        lineHeight: 'base',
        fontFamily: 'body',
      },
    }),
  },
  components: {
    Button: {
      baseStyle: {
        borderRadius: 'full',
        _focus: {
          boxShadow: '0 0 0 3px rgba(228, 87, 46, 0.6)', // Rocket Red glow on focus
        },
      },
      variants: {
        solid: (props) => ({
          bg: 'brand.accent',
          color: 'white',
          _hover: {
            bg: 'brand.secondaryAccent',
            transform: 'scale(1.05)',
            transition: 'all 0.2s ease-in-out',
          },
          _active: {
            bg: 'brand.secondaryAccent',
          },
        }),
        ghost: (props) => ({
          color: 'brand.500', // Light text for ghost buttons
          _hover: {
            bg: 'brand.700', // Muted background on hover
            color: 'brand.accent', // Accent color on text hover
            transform: 'scale(1.05)',
            transition: 'all 0.2s ease-in-out',
          },
          _active: {
            bg: 'brand.600',
          },
        }),
      },
    },
    Link: {
      baseStyle: {
        color: 'brand.accent',
        _hover: {
          textDecoration: 'none',
          color: 'brand.secondaryAccent',
          transition: 'color 0.2s ease-in-out',
        },
      },
    },
    Heading: {
      baseStyle: {
        fontFamily: 'heading', // Use the Space Mono font
        letterSpacing: 'wide', // Add subtle letter spacing
      },
    },
    Text: {
      baseStyle: {
        fontFamily: 'body', // Use the Outfit font
      },
    },
  },
  config: {
    initialColorMode: 'dark',
    useSystemColorMode: false,
  },
});


function App() {
  const [artistData, setArtistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      if (appData) {
        setArtistData(appData);
        setLoading(false);
      } else {
        setError("Artist data could not be loaded from data.json.");
        setLoading(false);
      }
    } catch (err) {
      console.error("Failed to load application data:", err);
      setError("An error occurred while parsing website content. Please check data.json format.");
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <ChakraProvider theme={customTheme}>
        <Flex align="center" justify="center" height="100vh" direction="column" bg="space.deepBlue">
          <Spinner size="xl" color="brand.accent" thickness="4px" />
          <Text mt={4} fontSize="lg" color="brand.500" fontFamily="body">Loading your creative universe...</Text>
        </Flex>
      </ChakraProvider>
    );
  }

  if (error) {
    return (
      <ChakraProvider theme={customTheme}>
        <Flex align="center" justify="center" height="100vh" direction="column" bg="space.deepBlue">
          <Text color="red.500" fontSize="xl" textAlign="center" fontFamily="body">
            Error: {error}
          </Text>
          <Text mt={2} color="brand.600" fontSize="md" fontFamily="body">
            Ensure `data.json` exists in `src/` and is valid JSON.
          </Text>
        </Flex>
      </ChakraProvider>
    );
  }

  if (!artistData) {
    return (
      <ChakraProvider theme={customTheme}>
        <Flex align="center" justify="center" height="100vh" direction="column" bg="space.deepBlue">
          <Text color="red.500" fontSize="xl" textAlign="center" fontFamily="body">
            No artist data available. The `data.json` file might be empty or malformed.
          </Text>
        </Flex>
      </ChakraProvider>
    );
  }

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <ChakraProvider theme={customTheme}>
      <Box minH="100vh">
        <Navbar artistName={artistData.name} scrollToSection={scrollToSection} />

        <VStack spacing={{ base: 16, md: 24 }} align="stretch" py={{ base: 8, md: 12 }} px={{ base: 4, md: 8, lg: 16 }}>
          <Box id="home">
            <Hero
              artistName={artistData.name}
              tagline={artistData.tagline}
              logoUrl={artistData.logoUrl}
              socialLinks={artistData.socialLinks}
            />
          </Box>

          <Box id="music">
            <MusicSection music={artistData.music} />
          </Box>

          <Box id="videos">
            <VideoSection videos={artistData.videos} />
          </Box>
        </VStack>

        <Footer socialLinks={artistData.socialLinks} contactEmail={artistData.contactEmail} />
      </Box>
    </ChakraProvider>
  );
}

export default App;